import {
  forwardRef,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { useCaptionLabel } from './useCaptionLabel'
import { cn } from '@/shared/lib/utils'
import {
  clampNote,
  decimalsForStep,
  emittedNumeric,
  normalizeNumeric,
  sanitizeNumeric,
  stepAmount,
} from './numeric'

function toBound(value: number | string | undefined): number | undefined {
  if (value === undefined || value === null || value === '') return undefined
  const parsed = typeof value === 'number' ? value : parseFloat(value)
  return isFinite(parsed) ? parsed : undefined
}

/** Mirrors Input's label rendering so a trailing `*` picks up the red asterisk. */
function renderRequiredLabel(text: string) {
  const trimmed = text.trimEnd()
  if (!trimmed.endsWith('*')) return text
  return (
    <>
      {trimmed.slice(0, -1).trimEnd()}
      <span className="ml-0.5 text-red-500">*</span>
    </>
  )
}

export interface NumericInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'min' | 'max' | 'step'
> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: ReactNode
  rightElement?: ReactNode
  /** Accepts the string form too, since that is how JSX often spells it. */
  min?: number | string
  max?: number | string
  step?: number | string
  /**
   * Digits allowed after the point. Defaults to whatever `step` implies, so a
   * `step={0.01}` money field takes two and a bare count field takes none.
   */
  decimals?: number
  allowNegative?: boolean
  /**
   * Select the whole value when the field gains focus, so typing replaces a
   * prefilled figure instead of appending to it ("2" then "1" read "21").
   */
  selectOnFocus?: boolean
}

/**
 * Numeric field that only ever holds a number.
 *
 * Drop-in for `<Input type="number" />`: the same label/error/hint/icon props,
 * and `onChange` still receives an event whose `target.value` is the field's
 * text. The difference is that the text can no longer be `"1e5"`, `"12abc"` or
 * the empty string the browser substitutes for anything it cannot parse.
 */
export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(function NumericInput(
  {
    label,
    error,
    hint,
    leftIcon,
    rightElement,
    min,
    max,
    step,
    decimals,
    allowNegative = false,
    selectOnFocus = false,
    className,
    id,
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseUp,
    ...props
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [draft, setDraft] = useState<string | null>(null)
  const [note, setNote] = useState<string | undefined>(undefined)
  // Set by a select-on-focus so the click's own mouseup does not collapse
  // the selection it just made.
  const keepSelection = useRef(false)
  useCaptionLabel(inputRef)

  const places = decimals ?? decimalsForStep(step)
  const format = { decimals: places, allowNegative }
  const bounds = { min: toBound(min), max: toBound(max) }

  const autoId = useId()
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-') ?? autoId
  const shown = draft ?? (value === undefined || value === null ? '' : String(value))
  const message = error ?? note
  const describedBy = message ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined

  function attachRef(node: HTMLInputElement | null) {
    inputRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  /** Push a value the operator did not type (blur settle, arrow step) upstream. */
  function emit(next: string) {
    const node = inputRef.current
    if (!node) return
    node.value = next
    onChange?.({
      ...({} as ChangeEvent<HTMLInputElement>),
      target: node,
      currentTarget: node,
    } as ChangeEvent<HTMLInputElement>)
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const clean = sanitizeNumeric(event.target.value, format)
    setDraft(clean)
    setNote(undefined)
    // Report a safe value upstream (never a lone sign or below the floor) while
    // the box keeps showing what was typed; blur settles and explains it.
    const node = event.target
    node.value = emittedNumeric(clean, { allowNegative, ...bounds })
    onChange?.(event)
    node.value = clean
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    const typed = event.target.value
    const settled = normalizeNumeric(typed, { ...format, ...bounds })
    setNote(clampNote(typed, settled, { allowNegative, ...bounds }))
    setDraft(null)
    if (settled !== typed) emit(settled)
    onBlur?.(event)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      const delta = stepAmount(step, places) * (event.key === 'ArrowUp' ? 1 : -1)
      const base = parseFloat(shown)
      const next = normalizeNumeric(String((isFinite(base) ? base : 0) + delta), {
        ...format,
        ...bounds,
      })
      setDraft(next)
      setNote(undefined)
      emit(next)
    }
    onKeyDown?.(event)
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
          {renderRequiredLabel(label)}
        </label>
      )}
      <div className="relative flex items-center">
        {leftIcon && (
          <span className="pointer-events-none absolute left-3 flex items-center text-gray-400">
            {leftIcon}
          </span>
        )}
        <input
          {...props}
          ref={attachRef}
          id={inputId}
          type="text"
          inputMode={places > 0 ? 'decimal' : 'numeric'}
          autoComplete={props.autoComplete ?? 'off'}
          value={shown}
          onChange={handleChange}
          onFocus={(event) => {
            setNote(undefined)
            if (selectOnFocus) {
              event.currentTarget.select()
              keepSelection.current = true
            }
            onFocus?.(event)
          }}
          onMouseUp={(event) => {
            if (keepSelection.current) {
              keepSelection.current = false
              event.preventDefault()
            }
            onMouseUp?.(event)
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            'block w-full h-9 rounded-lg border bg-white px-3.5 text-sm text-gray-900 tabular-nums',
            'placeholder:text-gray-400 transition-shadow duration-150',
            'focus:outline-none focus:border-brand-orange focus:shadow-input-focus',
            error
              ? 'border-red-400 focus:border-red-400 focus:shadow-none'
              : 'border-gray-200 hover:border-gray-300',
            leftIcon && 'pl-9',
            rightElement && 'pr-10',
            className,
          )}
          aria-invalid={!!error}
          aria-describedby={describedBy}
        />
        {rightElement && <span className="absolute right-3 flex items-center">{rightElement}</span>}
      </div>
      {message && (
        <p
          id={`${inputId}-error`}
          className={cn('text-xs', error ? 'text-red-600' : 'text-amber-600')}
          role="alert"
        >
          {message}
        </p>
      )}
      {hint && !message && (
        <p id={`${inputId}-hint`} className="text-xs text-gray-500">
          {hint}
        </p>
      )}
    </div>
  )
})
